/*
  Incidents for the Other Platforms popup, worded for someone who does not read
  indicator codes. The popup shows the technical value next to the plain one, so
  every helper here translates and none of them hides the original.
*/

import type { IncidentIndicator, IncidentRow, IncidentsPage } from './api/deep-dive.types'

export type IncidentWindow = '24h' | '7d' | '30d'
export const INCIDENT_WINDOWS: readonly IncidentWindow[] = ['24h', '7d', '30d']

const WINDOW_HOURS: Record<IncidentWindow, number> = { '24h': 24, '7d': 24 * 7, '30d': 24 * 30 }

const WINDOW_LABEL: Record<IncidentWindow, string> = {
  '24h': 'last 24 hours',
  '7d': 'last 7 days',
  '30d': 'last 30 days',
}

export function windowStart(period: IncidentWindow, now = Date.now()): string {
  return new Date(now - WINDOW_HOURS[period] * 3_600_000).toISOString()
}

export function windowLabel(period: IncidentWindow): string {
  return WINDOW_LABEL[period]
}

export type IncidentCategory = 'outage' | 'major' | 'unreachable' | 'minor' | 'slow'

/** Worst first: the order groups and breakdowns are listed in. */
export const CATEGORY_ORDER: readonly IncidentCategory[] = [
  'outage',
  'major',
  'unreachable',
  'minor',
  'slow',
]

export const CATEGORIES: Record<
  IncidentCategory,
  { label: string; explain: string; dot: string; text: string }
> = {
  outage: {
    label: 'Outage',
    explain: 'The platform was down. People could not use it.',
    dot: 'bg-critical-bright',
    text: 'text-critical-bright',
  },
  major: {
    label: 'Major problem',
    explain:
      'Partly broken: the platform returned server errors, or its vendor reported a major issue.',
    dot: 'bg-degraded-dim',
    text: 'text-degraded',
  },
  unreachable: {
    label: "Couldn't reach",
    explain:
      'Our checker got no answer, or could not read the status page. Often a network blip between us and the vendor rather than a real outage.',
    dot: 'bg-fg-subtle',
    text: 'text-fg-muted',
  },
  minor: {
    label: 'Minor issue / maintenance',
    explain:
      'The vendor reported a small problem or planned maintenance. Most things kept working.',
    dot: 'bg-degraded',
    text: 'text-degraded',
  },
  slow: {
    label: 'Slow',
    explain: 'It worked, but answered slower than usual.',
    dot: 'bg-info',
    text: 'text-info',
  },
}

const BY_INDICATOR: Record<IncidentIndicator, IncidentCategory> = {
  CRITICAL_FAIL: 'outage',
  DEGRADED: 'major',
  VENDOR_SILENT: 'unreachable',
  MINOR: 'minor',
  MAINTENANCE: 'minor',
  LATENCY_HIGH: 'slow',
}

export function incidentCategory(row: Pick<IncidentRow, 'indicator'>): IncidentCategory {
  return BY_INDICATOR[row.indicator] ?? 'minor'
}

/** The HTTP code that confirmed the incident: the probe's own, else the one quoted in the cause. */
export function incidentHttpStatus(row: IncidentRow): number | undefined {
  const probed = row.evidence?.find((e) => typeof e.http_status === 'number')?.http_status
  if (probed) return probed
  const quoted = /HTTP (\d{3})/.exec(row.cause_summary ?? '')
  return quoted ? Number(quoted[1]) : undefined
}

const HTTP_MEANING: Record<number, string> = {
  400: 'the request was rejected as invalid',
  401: 'login required',
  403: 'access refused',
  404: 'page not found',
  408: 'the request timed out',
  429: 'too many requests (rate limited)',
  500: 'an error inside the platform',
  502: 'a server in front of the platform got a bad answer from it',
  503: 'temporarily unavailable (overloaded or under maintenance)',
  504: 'a server in front of the platform timed out waiting for it',
}

export function httpMeaning(code: number): string {
  if (HTTP_MEANING[code]) return HTTP_MEANING[code]
  if (code >= 500) return 'a server error on the platform side'
  if (code >= 400) return 'the request was refused'
  return 'an unexpected answer'
}

/** Go's network errors, as the probe quotes them, in words. */
const NETWORK_CAUSES: [RegExp, string][] = [
  [/deadline exceeded|timeout|timed out/i, 'Timed out: no answer within our time limit.'],
  [/connection reset/i, 'The connection was cut off midway.'],
  [/connection refused/i, 'The platform refused the connection.'],
  [/no such host|server misbehaving/i, "The platform's web address could not be looked up (DNS)."],
  [/certificate|x509|tls:/i, 'The secure connection (TLS certificate) failed.'],
  [
    /no matching components|invalid character|decode/i,
    "We could not read the vendor's status page.",
  ],
  [/\bEOF\b/, 'The connection closed before any answer came back.'],
]

/** One plain sentence on why the incident happened. */
export function plainCause(row: IncidentRow): string {
  const category = incidentCategory(row)
  if (category === 'slow') return 'Responses were slower than our limit.'
  const raw = row.cause_summary ?? ''
  const vendor = /^Vendor-reported: (.+)$/.exec(raw)
  if (vendor) return `The vendor's status page said: ${vendor[1]}`
  const code = incidentHttpStatus(row)
  if (code) return `The platform answered HTTP ${code}: ${httpMeaning(code)}.`
  for (const [pattern, text] of NETWORK_CAUSES) if (pattern.test(raw)) return text
  return raw || CATEGORIES[category].explain
}

/** "45s", "12m 05s", "2h 05m", "3d 4h". */
export function shortDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  const pad = (n: number) => String(n).padStart(2, '0')
  if (total < 60) return `${total}s`
  const mins = Math.floor(total / 60)
  if (mins < 60) return `${mins}m ${pad(total % 60)}s`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ${pad(mins % 60)}m`
  return `${Math.floor(hours / 24)}d ${hours % 24}h`
}

/** "lasted 2m 05s", or "ongoing for 1h 20m" while it is still open. */
export function incidentDuration(row: IncidentRow, now = Date.now()): string {
  if (!row.ongoing && row.duration_ms !== null) return `lasted ${shortDuration(row.duration_ms)}`
  return `ongoing for ${shortDuration(now - Date.parse(row.started_at))}`
}

/** Vendor status-page links the evidence cites, without repeats. */
export function vendorLinks(row: IncidentRow): string[] {
  return [...new Set((row.evidence ?? []).flatMap((e) => (e.url ? [e.url] : [])))]
}

export const EVIDENCE_SOURCE: Record<string, string> = {
  poll: 'Our check',
  indicator: 'Rule',
  vendor_status: 'Vendor status page',
}

export interface IncidentSummary {
  /** Incidents in the window, as the server counted them. */
  total: number
  /** Breakdown of the incidents fetched, which is all of them unless `total` is larger. */
  byCategory: Partial<Record<IncidentCategory, number>>
  last?: IncidentRow
}

function countByCategory(rows: IncidentRow[]): Partial<Record<IncidentCategory, number>> {
  const counts: Partial<Record<IncidentCategory, number>> = {}
  for (const row of rows) {
    const c = incidentCategory(row)
    counts[c] = (counts[c] ?? 0) + 1
  }
  return counts
}

export function summarizeIncidents(page: IncidentsPage): IncidentSummary {
  // The route returns newest first.
  return { total: page.total, byCategory: countByCategory(page.incidents), last: page.incidents[0] }
}

export type IncidentGrouping = 'issue' | 'platform'

export interface IncidentGroup {
  key: string
  label: string
  /** Plain explanation of the issue type, or the platform's breakdown. */
  explain: string
  dot: string
  rows: IncidentRow[]
}

const newestFirst = (a: IncidentRow, b: IncidentRow) =>
  Date.parse(b.started_at) - Date.parse(a.started_at)

/** "3 slow · 1 couldn't reach", worst first. */
export function breakdownText(byCategory: Partial<Record<IncidentCategory, number>>): string {
  return CATEGORY_ORDER.filter((c) => byCategory[c])
    .map((c) => `${byCategory[c]} ${CATEGORIES[c].label.toLowerCase()}`)
    .join(' · ')
}

/**
 * Issue-type groups come worst first. Platform groups come by their most recent
 * incident, so whatever just happened is at the top either way.
 */
export function groupIncidents(
  rows: IncidentRow[],
  by: IncidentGrouping,
  nameOf: (service: string) => string,
): IncidentGroup[] {
  const sorted = [...rows].sort(newestFirst)
  if (by === 'issue') {
    return CATEGORY_ORDER.map((c) => ({
      key: c,
      label: CATEGORIES[c].label,
      explain: CATEGORIES[c].explain,
      dot: CATEGORIES[c].dot,
      rows: sorted.filter((r) => incidentCategory(r) === c),
    })).filter((g) => g.rows.length > 0)
  }

  const byService = new Map<string, IncidentRow[]>()
  for (const row of sorted) byService.set(row.service, [...(byService.get(row.service) ?? []), row])
  return [...byService].map(([service, list]) => {
    const byCategory = countByCategory(list)
    const worst = CATEGORY_ORDER.find((c) => byCategory[c]) ?? 'minor'
    return {
      key: service,
      label: nameOf(service),
      explain: breakdownText(byCategory),
      dot: CATEGORIES[worst].dot,
      rows: list,
    }
  })
}
